/* eslint-disable max-lines */
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router";
import {StoreHelpers} from "react-joyride";
import ReactTour, {TourStep} from "../../components/ReactTour";
import {avatarsQueryData, queryData} from "./challenge-edit.graphql";
import {
  createDummyAvatarAction,
  createDummyChoiceUserInputAction,
  createDummyChoiceUserInputBranch,
  createDummyChallenge,
  createDummyScenario,
  createDummySystemAction,
  createDummyUserInputAction,
} from "../../dummies";
import {getLanguageCode} from "../../i18n";
import {useTour} from "../../providers/tour";
import {Action, ChoiceUserInputBranchState} from "../../schema";
import {getTargetsClassNamesWithPrefix, targetClassNamesToSelectors, TourClassesMock} from "../../tours";
import EditableItemName from "../../components/editor/EditableItemName";

export const NAME = "editorChallengeEdit";

/// Targets
type TargetNames = [
  "challengeName",
  "scenarios",
  "evaluateScenarios",
  "avatarAction",
  "timeline",
  "systemAction",
  "userInputAction",
  "choiceUserInputAction",
  "addActionRow",
  "challengeCustomizeForm",
  "avatarSelect",
  "videoCallMode",
  "language",
  "recognitionEngine",
  "recognitionAccuracy",
  "badWordsFilter",
  "triggerDicts",
  "generateVideo",
  "detailedResults",
  "evaluateScenarios",
  "inviteByLink",
  "characterErrors",
  "muteVoices",
  "showPreviousPhrase",
  "background",
  "topRightMenu",
  "participants",
  "scorm",
  "shareButton",
  "preview",
  "challengeInput",
  "challengeType",
];
type TargetName = TargetNames[number];
const targetNames: TargetNames = [
  "challengeName",
  "scenarios",
  "evaluateScenarios",
  "avatarAction",
  "timeline",
  "systemAction",
  "userInputAction",
  "choiceUserInputAction",
  "addActionRow",
  "challengeCustomizeForm",
  "avatarSelect",
  "videoCallMode",
  "language",
  "recognitionEngine",
  "recognitionAccuracy",
  "badWordsFilter",
  "triggerDicts",
  "generateVideo",
  "detailedResults",
  "evaluateScenarios",
  "inviteByLink",
  "characterErrors",
  "muteVoices",
  "showPreviousPhrase",
  "background",
  "topRightMenu",
  "participants",
  "scorm",
  "shareButton",
  "preview",
  "challengeInput",
  "challengeType",
];

export type Targets = Record<TargetName, TourStep["target"]>;

export const classes = getTargetsClassNamesWithPrefix(targetNames, "tour_editor_challengeEdit_");

/// Types
type MockByAction = TourClassesMock<"item">;

type MockDynamicData = {
  challengeName?: string;
};

export type EditorChallengeEditTourMock = {
  tour: "editorChallengeEdit";
  mockBy: {
    action: Map<Action["id"], MockByAction>;
  };

  nameEditSave: React.ComponentProps<typeof EditableItemName>["onSave"];
  data: MockDynamicData;
};

export function isEditorChallengeEditTourMock(mock: any): mock is EditorChallengeEditTourMock {
  return mock?.tour === NAME;
}

/// Component
type props = {
  loading: boolean;
  targets?: Partial<Targets>;
  returnToPreviousPage?: boolean;
};

export default function EditorChallengeEditTour({loading, targets: componentTargets, returnToPreviousPage}: props) {
  const history = useHistory();

  const {t} = useTranslation("tours");
  const {end: endTour, setMock} = useTour();

  const onTourEnd = useCallback(() => {
    if (returnToPreviousPage) {
      history.goBack();
      return true;
    }
    return endTour(NAME);
  }, [returnToPreviousPage, history, endTour]);

  useEffect(
    () => () => {
      endTour(NAME);
    },
    [endTour],
  );

  const [helpers, setHelpers] = useState<StoreHelpers>();

  const [data, setData] = useState<EditorChallengeEditTourMock["data"]>({});

  const targets = useMemo(
    () => ({
      ...targetClassNamesToSelectors(classes),
      ...componentTargets,
    }),
    [componentTargets],
  );

  const actionMocks = useMemo<Map<Action["id"], MockByAction>>(() => {
    const actions = ids.challenge.scenarios[0].actions;
    return new Map([
      [
        actions[0].id,
        {
          classes: {
            item: classes.avatarAction,
          },
        },
      ],
      [
        actions[1].id,
        {
          classes: {
            item: classes.systemAction,
          },
        },
      ],
      [
        actions[2].id,
        {
          classes: {
            item: classes.userInputAction,
          },
        },
      ],
      [
        actions[4].id,
        {
          classes: {
            item: classes.choiceUserInputAction,
          },
        },
      ],
    ]);
  }, []);

  const mock = useMemo<Omit<EditorChallengeEditTourMock, "tour" | "mockBy" | "data">>(
    () => ({
      nameEditSave: ({name}) => {
        setData((prevData) => ({...prevData, challengeName: name}));
        setTimeout(() => helpers?.next());
      },
    }),
    [helpers],
  );

  useEffect(() => {
    setMock({
      ...mock,
      data,
      tour: NAME,
      mockBy: {
        action: actionMocks,
      },
    });
    return () => setMock(undefined);
  }, [mock, data, actionMocks, setMock]);

  const steps = useMemo<TourStep[]>(
    () => [
      {
        title: t("editorChallengeEdit.start.title"),
        content: t("editorChallengeEdit.start.content"),
        target: "body",
        placement: "center",
      },
      {
        title: t("editorChallengeEdit.challengeName.title"),
        content: t("editorChallengeEdit.challengeName.content"),
        target: targets.challengeName,
        placement: "bottom",
        disableNextButton: true,
        spotlightClicks: true,
      },
      {
        title: t("editorChallengeEdit.challengeNameUpdated.title"),
        content: t("editorChallengeEdit.challengeNameUpdated.content"),
        target: targets.challengeName,
        placement: "bottom",
      },
      {
        title: t("editorChallengeEdit.scenarios.title"),
        content: t("editorChallengeEdit.scenarios.content"),
        target: targets.scenarios,
        placement: "bottom",
      },
      // Actions
      {
        title: t("editorChallengeEdit.timeline.title"),
        content: t("editorChallengeEdit.timeline.content"),
        target: targets.timeline,
        placement: "right",
      },
      {
        title: t("editorChallengeEdit.avatarAction.title"),
        content: t("editorChallengeEdit.avatarAction.content"),
        target: targets.avatarAction,
        placement: "right",
      },
      {
        title: t("editorChallengeEdit.systemAction.title"),
        content: t("editorChallengeEdit.systemAction.content"),
        target: targets.systemAction,
        placement: "right",
      },
      {
        title: t("editorChallengeEdit.userInputAction.title"),
        content: t("editorChallengeEdit.userInputAction.content"),
        target: targets.userInputAction,
        placement: "right",
      },
      {
        title: t("editorChallengeEdit.choiceUserInputAction.title"),
        content: t("editorChallengeEdit.choiceUserInputAction.content"),
        target: targets.choiceUserInputAction,
        placement: "right",
      },
      {
        title: t("editorChallengeEdit.addActionRow.title"),
        content: t("editorChallengeEdit.addActionRow.content"),
        target: targets.addActionRow,
        placement: "right",
      },
      // TODO: Let user add action and edit. Same for each one
      // Challenge preferences
      {
        title: t("editorChallengeEdit.challengeCustomizeForm.title"),
        content: t("editorChallengeEdit.challengeCustomizeForm.content"),
        target: targets.challengeCustomizeForm,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.avatarSelect.title"),
        content: t("editorChallengeEdit.avatarSelect.content"),
        target: targets.avatarSelect,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.videoCallMode.title"),
        content: t("editorChallengeEdit.videoCallMode.content"),
        target: targets.videoCallMode,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.challengeType.title"),
        content: t("editorChallengeEdit.challengeType.content"),
        target: targets.challengeType,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.challengeInput.title"),
        content: t("editorChallengeEdit.challengeInput.content"),
        target: targets.challengeInput,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.language.title"),
        content: t("editorChallengeEdit.language.content"),
        target: targets.language,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.recognitionEngine.title"),
        content: t("editorChallengeEdit.recognitionEngine.content"),
        target: targets.recognitionEngine,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.recognitionAccuracy.title"),
        content: t("editorChallengeEdit.recognitionAccuracy.content"),
        target: targets.recognitionAccuracy,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.badWordsFilter.title"),
        content: t("editorChallengeEdit.badWordsFilter.content"),
        target: targets.badWordsFilter,
        placement: "left",
      },

      {
        title: t("editorChallengeEdit.triggerDicts.title"),
        content: t("editorChallengeEdit.triggerDicts.content"),
        target: targets.triggerDicts,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.generateVideo.title"),
        content: t("editorChallengeEdit.generateVideo.content"),
        target: targets.generateVideo,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.detailedResults.title"),
        content: t("editorChallengeEdit.detailedResults.content"),
        target: targets.detailedResults,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.evaluateScenarios.title"),
        content: t("editorChallengeEdit.evaluateScenarios.content"),
        target: targets.evaluateScenarios,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.inviteByLink.title"),
        content: t("editorChallengeEdit.inviteByLink.content"),
        target: targets.inviteByLink,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.characterErrors.title"),
        content: t("editorChallengeEdit.characterErrors.content"),
        target: targets.characterErrors,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.muteVoices.title"),
        content: t("editorChallengeEdit.muteVoices.content"),
        target: targets.muteVoices,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.showPreviousPhrase.title"),
        content: t("editorChallengeEdit.showPreviousPhrase.content"),
        target: targets.showPreviousPhrase,
        placement: "left",
      },
      {
        title: t("editorChallengeEdit.background.title"),
        content: t("editorChallengeEdit.background.content"),
        target: targets.background,
        placement: "left",
      },
      // Top right menu
      {
        title: t("editorChallengeEdit.topRightMenu.title"),
        content: t("editorChallengeEdit.topRightMenu.content"),
        target: targets.topRightMenu,
        placement: "bottom",
      },
      {
        title: t("editorChallengeEdit.participants.title"),
        content: t("editorChallengeEdit.participants.content"),
        target: targets.participants,
        placement: "bottom",
      },
      {
        title: t("editorChallengeEdit.scorm.title"),
        content: t("editorChallengeEdit.scorm.content"),
        target: targets.scorm,
        placement: "bottom",
      },
      {
        title: t("editorChallengeEdit.shareButton.title"),
        content: t("editorChallengeEdit.shareButton.content"),
        target: targets.shareButton,
        placement: "bottom",
      },
      {
        title: t("editorChallengeEdit.preview.title"),
        content: t("editorChallengeEdit.preview.content"),
        target: targets.preview,
        placement: "bottom",
        // spotlightClicks: true
      },
    ],
    [t, targets],
  );

  return (
    <ReactTour
      continuous
      run={!loading}
      steps={steps}
      onTourEnd={onTourEnd}
      getHelpers={setHelpers}
      scrollToFirstStep
      disableOverlayClose
      disableCloseOnEsc
      showSkipButton
      hideCloseButton
    />
  );
}

/// IDs of mock data to refer
const ids = {
  challenge: {
    id: "editorChallengeEditTour_Challenge_1",
    scenarios: [
      {
        id: "editorChallengeEditTour_Scenario_1",
        actions: [
          {id: "editorChallengeEditTour_AvatarAction_1"},
          {id: "editorChallengeEditTour_SystemAction_1"},
          {id: "editorChallengeEditTour_UserInputAction_1"},
          {id: "editorChallengeEditTour_AvatarAction_2"},
          {id: "editorChallengeEditTour_ChoiceUserInputAction_1"},
        ],
      },
    ],
  },
};

/// Mock data to show during tour
export function useMockData(avatars?: avatarsQueryData["avatars"], challengeName?: string): Partial<queryData> {
  const {t, i18n} = useTranslation("tours");
  const languageCode = getLanguageCode(i18n.language);

  const challenge = useMemo(
    () =>
      createDummyChallenge({
        name: challengeName ?? t("editorChallengeEdit.mockData.challenges.1.name"),
        id: ids.challenge.id,
        languageCode,
        lastUpdatedAt: Date.UTC(2020, 10, 16, 8, 36) / 1000,
        avatar: avatars && avatars[0],
      }),
    [avatars, languageCode, challengeName, t],
  );

  const scenarios = useMemo(() => {
    const scenariosActions = ids.challenge.scenarios.map(({actions}) => actions);
    return [
      createDummyScenario({
        isMain: true,
        id: ids.challenge.scenarios[0].id,
        actions: [
          createDummyAvatarAction({
            text: t("editorChallengeEdit.mockData.scenario.1.action.1"),
            id: scenariosActions[0][0].id,
          }),
          createDummySystemAction({
            text: t("editorChallengeEdit.mockData.scenario.1.action.2"),
            id: scenariosActions[0][1].id,
          }),
          createDummyUserInputAction({
            referenceText: t("editorChallengeEdit.mockData.scenario.1.action.3"),
            id: scenariosActions[0][2].id,
          }),
          createDummyAvatarAction({
            text: t("editorChallengeEdit.mockData.scenario.1.action.4"),
            id: scenariosActions[0][3].id,
          }),
          createDummyChoiceUserInputAction({
            id: scenariosActions[0][4].id,
            branches: [
              createDummyChoiceUserInputBranch({
                state: ChoiceUserInputBranchState.INCORRECT,
                userInput: createDummyUserInputAction({
                  referenceText: t("editorChallengeEdit.mockData.scenario.1.action.5.branch.1.user"),
                }),
                systemReaction: createDummySystemAction({
                  text: t("editorChallengeEdit.mockData.scenario.1.action.5.branch.1.system"),
                }),
              }),
              createDummyChoiceUserInputBranch({
                state: ChoiceUserInputBranchState.CORRECT,
                userInput: createDummyUserInputAction({
                  referenceText: t("editorChallengeEdit.mockData.scenario.1.action.5.branch.2.user"),
                }),
                avatarReaction: createDummyAvatarAction({
                  text: t("editorChallengeEdit.mockData.scenario.1.action.5.branch.2.avatar"),
                }),
              }),
            ],
          }),
        ],
      }),
    ];
  }, [t]);

  return {
    challenge,
    project: undefined,
    scenarios,
  };
}
